import fetch from 'isomorphic-unfetch';
import { ApiTimeouts } from 'constants/timeouts';
import { FetchOptions, MiddlewareFunction } from 'types/fetchWithRetry';
import MiddlewareManager, { defaultMiddleware } from 'utils/fetchWithRetry';

/**
 * Fetch with timeout functionality.
 *
 * A wrapper around the standard  [fetch](https://developer.mozilla.org/docs/Web/API/fetch) function that adds a timeout.
 *
 * @param url The URL to fetch.
 * @param options Fetch options.
 * @param timeout Timeout in milliseconds. Default: {@link ApiTimeouts.DEFAULT_TIMEOUT}
 * @param middleware Middleware function to apply to the fetch options. If not provided, the global middleware or default middleware will be used.
 *
 * @returns The fetch response.
 *
 * @throws Throws an error if the request times out or fetch fails with an error.
 *
 * @example
 * // Basic usage with default timeout and middleware
 * const response = await fetchWithTimeout('https://api.example.com/data');
 *
 * @example
 * // Usage with custom timeout and middleware
 * const customMiddleware = async (url, options) => {
 *   options.headers = new Headers(options.headers);
 *   options.headers.append('Authorization', 'Bearer YOUR_TOKEN');
 *   return options;
 * };
 *
 * const response = await fetchWithTimeout('https://api.example.com/data', {}, 1000, customMiddleware);
 */
export const fetchWithTimeout = async (url: string, options: FetchOptions = {}, timeout: number = ApiTimeouts.DEFAULT_TIMEOUT, middleware: MiddlewareFunction = null): Promise<Response> => {
  const controller = new AbortController();
  const signal = controller.signal;

  // Use the provided middleware if set, otherwise fall back to global middleware, and then default middleware
  const globalMiddleware = MiddlewareManager.getGlobalMiddleware();
  const appliedMiddleware = middleware || globalMiddleware || defaultMiddleware;

  // Apply middleware to options
  const updatedOptions = await appliedMiddleware(url, options);
  updatedOptions.signal = signal;

  // Create a timeout promise
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => {
      controller.abort(); // Abort the fetch if timeout is reached
      reject(new Error(`Request to ${url} timed out after ${timeout}ms`));
    }, timeout)
  );

  const fetchPromise = fetch(url, updatedOptions);

  try {
    const response = await Promise.race([fetchPromise, timeoutPromise]);
    return response;
  } catch (error) {
    throw error;
  }
};